import { Injectable, inject, signal } from '@angular/core';
import { dfpHistoricalRequest } from '@interfaces/dfp/request/dfpIncomeStatementRequest';
import { dfpSubAccountLineResponse } from '@interfaces/dfp/response/dfpSubAccountLineResponse';
import { DfpColumn } from '@models/dfp/dfp-column.model';
import { DFPService } from '@services/http/dfp/dfp.service';
import { catchError, forkJoin, of, Subscription } from 'rxjs';

/**
 * Subcontas "dentre elas" da Análise Econômica (PBI 3423): carrega, junto com as tabelas, as linhas de subconta da
 * DRE e do Balanço e as guarda em signals que o template repassa às tabelas. Fica fora do `dfp.component` para não
 * engordar o componente (teto de ~1.000 linhas).
 *
 * Falha aqui não derruba a tela: sem a quebra, DRE e Balanço continuam com os mesmos números, só sem as linhas de
 * subconta. Só o time interno carrega (o endpoint do parceiro não expõe subconta), então o chamador pula em
 * `readOnly`.
 */
@Injectable({ providedIn: 'root' })
export class DfpSubAccountsStore {
  private readonly dfpService = inject(DFPService);
  private subscription?: Subscription;

  /** Subcontas da DRE; nulo enquanto não carregou ou quando falhou. */
  readonly income = signal<dfpSubAccountLineResponse[] | null>(null);

  /** Subcontas do Balanço; nulo enquanto não carregou ou quando falhou. */
  readonly balance = signal<dfpSubAccountLineResponse[] | null>(null);

  /** Recarrega as duas listas para as colunas pedidas em cada tabela; cancela a carga anterior. */
  load(params: dfpHistoricalRequest, periods: { income: DfpColumn[]; balance: DfpColumn[] }): void {
    this.clear();
    this.subscription = forkJoin({
      income: this.dfpService
        .getDFPHistoricalIncomeStatementSubAccounts(params, periods.income)
        .pipe(catchError(() => of(null))),
      balance: this.dfpService
        .getDFPHistoricalBalanceSheetSubAccounts(params, periods.balance)
        .pipe(catchError(() => of(null))),
    }).subscribe(result => {
      this.income.set(result.income);
      this.balance.set(result.balance);
    });
  }

  /** Esquece as listas (troca de empresa ou tela do parceiro). */
  clear(): void {
    this.subscription?.unsubscribe();
    this.income.set(null);
    this.balance.set(null);
  }
}
